// Locale-aware accessors for Click&Boat boat copy — the DOM sites' counterpart of
// apps/riviera/src/lib/i18n/boat-content.ts. A boat has no source description
// (the feed's is marketplace boilerplate), so the fallback for a boat that
// describe-boats hasn't written copy for yet is a template from its columns.

import i18n from 'i18next';

import type { Boat } from '@todo/core/boats';
import type { Locale } from '@todo/core/i18n/locales';

type BoatsResource = { typeLabel: Record<string, string>; fallbackTitle: string; fallbackDescription: string };

// The app's `boats` strings, read raw from the i18next store its
// src/lib/i18n/index.ts filled (initI18n) — templates filled here, not by t().
function strings(locale: Locale): BoatsResource {
  return i18n.getResource(locale, 'translation', 'boats') as BoatsResource;
}

function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] ?? '');
}

/** Localized label of a boat_type value, falling back to the raw value. */
export function localizedBoatType(boat: Boat, locale: Locale): string {
  const map = strings(locale).typeLabel;
  return (boat.boat_type && map[boat.boat_type]) || boat.boat_type || '';
}

function vars(boat: Boat, locale: Locale) {
  return { type: localizedBoatType(boat, locale), name: boat.name };
}

/** Localized title (plus describe-boats' uniqueness suffix), else a template. */
export function localizedBoatTitle(boat: Boat, locale: Locale): string {
  const base =
    boat.translations?.[locale]?.title ?? fill(strings(locale).fallbackTitle, vars(boat, locale)).trim();
  const suffix = boat.translations?._title_suffix?.[locale];
  return suffix ? `${base} · ${suffix}` : base;
}

/** Localized description, else a template. */
export function localizedBoatDescription(boat: Boat, locale: Locale): string {
  return (
    boat.translations?.[locale]?.description ??
    fill(strings(locale).fallbackDescription, vars(boat, locale)).trim()
  );
}

/** Localized ≤160-char summary, else the template description. */
export function localizedBoatSummary(boat: Boat, locale: Locale): string {
  return boat.translations?.[locale]?.summary ?? localizedBoatDescription(boat, locale);
}
